import { useState } from 'react'
import { DailyTotals } from '@/types'
import { formatCurrency } from '@/lib/utils'

interface RevenueConfirmationProps {
  metrics: DailyTotals
  selectedDate: string
  crmRevenue?: number | null
  currency?: string
  onConfirm: () => Promise<void>
  onCancel: () => void
  loading?: boolean
}

export function RevenueConfirmation({
  metrics,
  selectedDate,
  crmRevenue = null,
  currency = 'RUB',
  onConfirm,
  onCancel,
  loading = false,
}: RevenueConfirmationProps) {
  const [confirming, setConfirming] = useState(false)

  const handleConfirm = async () => {
    if (document.activeElement && document.activeElement instanceof HTMLElement) {
      document.activeElement.blur()
    }
    setConfirming(true)
    try {
      await onConfirm()
    } finally {
      setConfirming(false)
    }
  }

  const toNumber = (amount: number | string | null | undefined) => {
    if (amount === null || amount === undefined || amount === '') return 0
    const v = typeof amount === 'number'
      ? amount
      : Number(String(amount).replace(/\u00A0/g, ' ').replace(/\s/g, '').replace(',', '.'))
    return Number.isFinite(v) ? v : 0
  }

  const fmt = (amount: number | string | null | undefined) => formatCurrency(toNumber(amount), currency)

  const fmtCount = (v: number | string | null | undefined) => {
    const n = toNumber(v)
    return new Intl.NumberFormat('ru-RU').format(n)
  }

  const formatDate = (dateString: string) => {
    try {
      if (dateString?.includes('-')) {
        return new Date(dateString + 'T00:00:00').toLocaleDateString('ru-RU')
      }
      return dateString || 'Неизвестная дата'
    } catch {
      return 'Неизвестная дата'
    }
  }

  const revenueComponents = [
    { label: 'Новые продажи',                amount: metrics.new_sales_amount },
    { label: 'Оплаты пробного',              amount: metrics.trial_payments_amount },
    { label: 'Новые подписки',               amount: metrics.new_subscriptions_amount },
    { label: 'Членские взносы (новые)',       amount: metrics.membership_fees_new_amount },
    { label: 'Продления',                    amount: metrics.renewals_amount },
    { label: 'Апгрейды',                     amount: metrics.upgrades_amount },
    { label: 'Возвращённые клиенты',         amount: metrics.returning_clients_amount },
    { label: 'Автопродления подписок',       amount: metrics.subscription_renewals_amount },
    { label: 'Переходы на подписку',         amount: metrics.subscription_upgrades_amount },
    { label: 'Возвращено на подписку',       amount: metrics.subscription_returning_clients_amount },
    { label: 'Членские взносы при переходах', amount: metrics.membership_fees_subscription_upgrades_amount },
    { label: 'Членские взносы БЧС',            amount: metrics.membership_fees_subscription_returning_amount },
    { label: 'Индивидуальные занятия',       amount: metrics.private_sessions_amount },
    { label: 'Прочие доходы',               amount: metrics.other_income },
    { label: 'Возвраты',                    amount: metrics.refunds_amount, isRefund: true },
  ].filter(c => toNumber(c.amount) !== 0)

  const hasCrmData = crmRevenue !== null && crmRevenue !== undefined
  const crmDelta = (metrics.gross_revenue || 0) - (crmRevenue ?? 0)
  const hasEnteredData = Object.entries(metrics).some(([key, value]) => {
    if (key.startsWith('gross_revenue_confirmed')) return false
    return value !== null && value !== undefined && value !== ''
  })

  return (
    <div className="fixed inset-0 z-50 flex flex-col justify-end sm:items-center sm:justify-center sm:px-4 sm:py-6" onClick={onCancel}>
      <div className="absolute inset-0 bg-black/40" />
      <div
        className="relative w-full bg-card rounded-t-2xl shadow-xl max-h-[80vh] flex flex-col animate-slide-up sm:max-w-2xl sm:rounded-2xl sm:animate-none"
        onClick={e => e.stopPropagation()}
      >
        {/* Handle */}
        <div className="flex justify-center pt-3 pb-1 sm:hidden">
          <div className="w-10 h-1 rounded-full bg-muted-foreground/30" />
        </div>

        {/* Title */}
        <div className="px-4 pb-3 border-b border-border">
          <h2 className="text-base font-semibold">
            Подтверждение выручки за {formatDate(selectedDate)}
          </h2>
        </div>

        {/* Scrollable content */}
        <div className="overflow-y-auto flex-1 px-4 py-3 space-y-3">

          {/* Активность */}
          <div className="bg-muted/20 rounded-lg border border-muted p-3">
            <div className="text-xs font-medium text-muted-foreground mb-2">Активность за день:</div>
            <div className="space-y-1.5">
              {[
                { label: 'Звонков',                  value: fmtCount(metrics.calls_count) },
                { label: 'Дозвонов',                 value: fmtCount(metrics.answered_calls_count) },
                { label: 'Лидов',                    value: fmtCount(metrics.leads_count) },
                { label: 'Записей',                  value: fmtCount(metrics.appointments_count) },
                { label: 'Активных клиентов',        value: fmtCount(metrics.total_clients_count) },
                { label: 'Расходы на рекламу',       value: fmt(metrics.marketing_expenses_amount) },
              ].map(row => (
                <div key={row.label} className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{row.label}</span>
                  <span className="font-medium">{row.value}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Детализация выручки */}
          {(revenueComponents.length > 0 || hasEnteredData) && (
            <div className="bg-muted/20 rounded-lg border border-muted p-3">
              <div className="text-xs font-medium text-muted-foreground mb-2">Детализация расчета выручки:</div>
              <div className="space-y-1.5">
                {revenueComponents.map((c, i) => (
                  <div key={i} className="flex justify-between text-sm">
                    <span className={c.isRefund ? 'text-red-600' : 'text-muted-foreground'}>{c.label}</span>
                    <span className={`font-medium ${c.isRefund ? 'text-red-600' : ''}`}>
                      {c.isRefund ? '' : '+'}{fmt(c.amount)}
                    </span>
                  </div>
                ))}

                <div className="border-t border-muted pt-2 mt-1 flex justify-between font-semibold">
                  <span>Итого</span>
                  <span>{fmt(metrics.gross_revenue)}</span>
                </div>

                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Сверка Listok CRM</span>
                  {!hasCrmData ? (
                    <span className={`font-medium ${crmDelta === 0 ? 'text-green-700' : 'text-rose-700'}`}>
                      {crmDelta >= 0 ? '+' : ''}{fmt(crmDelta)}
                    </span>
                  ) : crmDelta === 0 ? (
                    <span className="font-medium text-green-700">✔ совпадает</span>
                  ) : (
                    <span className={`font-medium ${crmDelta > 0 ? 'text-rose-700' : 'text-green-700'}`}>
                      {crmDelta >= 0 ? '+' : ''}{fmt(crmDelta)}
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Buttons */}
        <div className="px-4 py-3 border-t border-border flex gap-3">
          <button
            onClick={onCancel}
            disabled={confirming || loading}
            className="flex-1 px-4 py-2.5 border border-border rounded-lg text-sm text-muted-foreground hover:bg-muted/50 transition-colors disabled:opacity-50"
          >
            Отмена
          </button>
          <button
            onClick={handleConfirm}
            disabled={confirming || loading}
            className="flex-1 px-4 py-2.5 bg-green-500 text-white rounded-lg text-sm font-medium hover:bg-fixed-green-600 transition-colors disabled:opacity-50"
          >
            {confirming ? (
              <span className="flex items-center justify-center gap-2">
                <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                Подтверждаю...
              </span>
            ) : (
              '✓ Подтвердить'
            )}
          </button>
        </div>
      </div>
    </div>
  )
}
